import type { CreateEmailDto, EmailDto, EmailListDto, UpdateEmailDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';
import type { SettingDto } from '../settings/models';

@Injectable({
  providedIn: 'root',
})
export class EmailService {
  private restService = inject(RestService);
  apiName = 'Default';
  

  create = (input: CreateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/email',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createAndCopyAttachements = (input: CreateEmailDto, entityTypeFullName: string, entityRecordId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/and-copy-attachements/${entityRecordId}`,
      params: { entityTypeFullName },
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createAndSend = (input: CreateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/email/and-send',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createAndSendForBackgroundJob = (input: CreateEmailDto, notificationId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/email/and-send-for-background-job/${notificationId}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/email/${id}`,
    },
    { apiName: this.apiName,...config });
  

  download = (entityRecordId: string, entityTypeFullName: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/download/${entityRecordId}`,
      params: { entityTypeFullName },
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getAndSetGmailApiSettings = (status?: boolean, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SettingDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/and-set-gmail-api-settings',
      params: { status },
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailListDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<EmailDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<EmailListDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/email/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/schema',
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  send = (input: UpdateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/send',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  sendEmailViaGmailApi = (input: UpdateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/send-email-via-gmail-api',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  sendWithLayout = (input: UpdateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/email/send-with-layout',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/email/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateEmailDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EmailDto>({
      method: 'PUT',
      headers: { Accept: 'application/json' },
      url: `/api/app/email/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
}